import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

interface DialpadState {
  isOpen: boolean;
  isMinimized: boolean;
  open: () => void;
  close: () => void;
  minimize: () => void;
  restore: () => void;
  toggle: () => void;
}

const DialpadContext = createContext<DialpadState | null>(null);

export function DialpadStateProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const open = useCallback(() => {
    setIsOpen(true);
    setIsMinimized(false);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setIsMinimized(false);
  }, []);

  const minimize = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const restore = useCallback(() => {
    setIsMinimized(false);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen(prev => {
      if (!prev) setIsMinimized(false);
      return !prev;
    });
  }, []);

  return (
    <DialpadContext.Provider value={{ isOpen, isMinimized, open, close, minimize, restore, toggle }}>
      {children}
    </DialpadContext.Provider>
  );
}

export function useDialpadState() {
  const ctx = useContext(DialpadContext);
  if (!ctx) throw new Error("useDialpadState must be used within DialpadStateProvider");
  return ctx;
}
